import { Injectable, computed, inject, signal } from '@angular/core';
import { Subscription, interval } from 'rxjs';

import { FileStateService } from './file-state.service';
import { TaapService } from './taap.service';
import ERROR_MESSAGES from '../shared/constants/errors-message.const';
import { FileUploadConfig } from '../shared/constants/file-config.constant';
import LoaderService from '../shared/loading.service';
import { calculateStageProgress } from '../shared/utils';

export type UploadOrigin = 'conversation' | 'myUploads';

const chunkingStage = {
    startPercent: 0,
    endPercent: 60,
    durationSec: 120,
};

@Injectable({ providedIn: 'root' })
export class UploadService {
    private readonly taapService = inject(TaapService);
    private readonly fileStateService = inject(FileStateService);
    private readonly loaderService = inject(LoaderService);

    private _progress = signal(0);
    private _progressCompleted = computed(() => this._progress() >= 100);
    private progressAnimationHandle: number | null = null;
    private progressAnimationTarget = 0;

    private _showFileUpload = signal(false);
    private _actionsSuppressed = signal(false);
    private readonly _uploadOrigin = signal<UploadOrigin>('conversation');
    private timerInterval: any = null;
    private uploadSubscription = new Subscription();
    private pollingSubscription = new Subscription();
    private requestId = '';
    private readonly maxHits = 120;
    private readonly poolingTimeFrame = 10000;
    private hits = 0;
    private noRequestOngoing = true;
    private uploadElapsedTime = 0;

    _selectedFile = signal<File | null>(null);
    _selectedFileName = signal<string | null>(null);
    _selectedFileTemporaryConsent = signal<boolean>(false);
    _filesFromConversation = signal<File[]>([]);

    isUploading = signal<boolean>(false);
    elapsedTime = signal<number>(0);
    currentFile = signal<File | null>(null);

    /**
        Single source of truth for whether the attachment icon should stay disabled.
        `isUploading()` is folded in so unrelated code can’t accidentally re-enable it while any upload is still in flight;
        consumers should use this instead of the raw loader flag.
     */
    readonly attachmentDisabled = computed(
        () => this.isUploading() || this.loaderService.currentAttachmentDisabledFlag()
    );

    readonly uploadPhase = computed(() => {
        if (!this.isUploading()) {
            return 'idle';
        }
        const time = this.elapsedTime();

        if (time >= 45) {
            return 'delayed';
        }
        if (time >= 10) {
            return 'slow';
        }
        return 'initial';
    });

    readonly feedbackMessage = computed(() => {
        if (this._actionsSuppressed()) {
            return '';
        }
        switch (this.uploadPhase()) {
            case 'slow':
                return 'Still uploading – this can take a bit for larger files.';
            case 'delayed':
                return 'This is taking longer than usual. You can keep waiting, or try a smaller file.';
            default:
                return '';
        }
    });

    readonly showActions = computed(
        () => this.uploadPhase() === 'delayed' && !this._actionsSuppressed()
    );

    readonly showSharedProgressBar = computed(
        () => this.isUploading() && this._uploadOrigin() === 'myUploads'
    );

    readonly showConversationUploadProgress = computed(
        () => this.isUploading() && this._uploadOrigin() === 'conversation'
    );

    readonly isMyUploadsOrigin = computed(() => this._uploadOrigin() === 'myUploads');

    readonly isConversationOrigin = computed(() => this._uploadOrigin() === 'conversation');

    /**
     *
     * @param file
     */
    startUpload(file: File) {
        this.resetState();
        this.reset();
        this._actionsSuppressed.set(false);
        this.currentFile.set(file);
        this.isUploading.set(true);

        this.timerInterval = setInterval(() => {
            this.elapsedTime.update((t) => t + 1);
        }, 1000);
    }

    /**
     * Cancels the current upload and cleans up.
     */
    cancelUpload() {
        this.stopTimer();
        this.isUploading.set(false);
        this.currentFile.set(null);
        this.elapsedTime.set(0);
        this._actionsSuppressed.set(false);
        this._uploadOrigin.set('conversation');
        console.log('Upload cancelled.');
    }

    /**
     * Resets the timer without cancelling the file selection (optional utility).
     */
    resetState() {
        this.stopTimer();
        this.stopPolling();
        this.elapsedTime.set(0);
        this.isUploading.set(false);
        this._actionsSuppressed.set(false);
        this.noRequestOngoing = true;
        this.hits = 0;
        this.uploadElapsedTime = 0;
        this.requestId = '';
    }

    /**
     * Call this when the component is destroyed to prevent memory leaks.
     * Skipped while a My Uploads upload is in flight since that upload is
     * intentionally decoupled from any single page/component's lifecycle and
     * must keep polling regardless of navigation.
     */
    cleanup() {
        if (this.isMyUploadsOrigin() && this.isUploading()) {
            return;
        }
        this.stopTimer();
        this.stopPolling();
    }

    /**
     *
     */
    private stopTimer() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }

    /**
     *
     */
    private stopPolling() {
        this.pollingSubscription.unsubscribe();
        this.pollingSubscription = new Subscription();
    }

    /**
     *
     */
    completeUpload() {
        this.stopTimer();
        this.isUploading.set(false);
        this.elapsedTime.set(0);
        this.currentFile.set(null);
        this._actionsSuppressed.set(false);
        this._uploadOrigin.set('conversation');
    }

    /**
     * Hides the delayed-state actions after user chooses to keep waiting.
     */
    keepWaiting() {
        this._actionsSuppressed.set(true);
    }

    /**
     *
     * @param file
     */
    startMyUploadsUpload(file: File) {
        this.setUploadOrigin('myUploads');
        this.loaderService.setAttachmentDisabledFlag(true);
        this.startUpload(file);
        this.uploadSubscription.unsubscribe();
        this.uploadSubscription = new Subscription();

        const uploadRequest = this.taapService
            .uploadDocument(file, this.selectedFileTemporaryConsent)
            .subscribe({
                next: (event: any) => {
                    if (!event.body?.request_id) {
                        return;
                    }

                    this.requestId = event.body.request_id;
                    const { endPercent, startPercent, durationSec } = chunkingStage;
                    const initTimeSec = this.poolingTimeFrame / 1000 / 2;
                    const progress = calculateStageProgress(
                        initTimeSec,
                        durationSec,
                        startPercent,
                        endPercent
                    );
                    this.setProgress(progress);
                    this.uploadElapsedTime = 0;
                    this.initiatePolling(this.requestId);
                },
                error: ({ error }) => {
                    this.handleMyUploadsFailure(
                        error?.detail ? error.detail : ERROR_MESSAGES.GENERIC_ERROR
                    );
                },
            });

        this.uploadSubscription.add(uploadRequest);
    }

    /**
     *
     * @param referenceId
     */
    private initiatePolling(referenceId: string) {
        this.stopPolling();
        this.hits = 0;
        this.noRequestOngoing = true;
        this.pollingSubscription = interval(this.poolingTimeFrame).subscribe(() => {
            if (this.hits > this.maxHits) {
                this.stopPolling();
                return;
            }

            this.hits++;
            if (this.noRequestOngoing) {
                this.handleUploadProgress(referenceId);
            }
        });
    }

    /**
     *
     * @param referenceId
     */
    private handleUploadProgress(referenceId: string) {
        this.noRequestOngoing = false;

        this.taapService.getUploadSummary(referenceId).subscribe({
            next: (response: any) => {
                this.noRequestOngoing = true;
                const uploadSummary = response?.body?.uploadSummary;
                if (!uploadSummary?.length) {
                    return;
                }

                const { status, chunks = 0, completed_chunks = 0 } = uploadSummary[0];
                switch (status) {
                    case 'initiated':
                        this.handleInitiated();
                        break;
                    case 'in progress':
                        if (chunks > 0) {
                            this.handleInProgress(chunks, completed_chunks);
                        }
                        break;
                    case 'completed':
                        this.handleMyUploadsCompleted(chunks, completed_chunks);
                        break;
                    case 'failed':
                        this.handleMyUploadsFailure();
                        break;
                    case 'guardrail blocked':
                        this.handleMyUploadsFailure(
                            'Your document did not meet our content guidelines. Please review and try again.',
                            30000
                        );
                        break;
                    default:
                        break;
                }
            },
            error: (_err) => {
                this.handleMyUploadsFailure(
                    _err?.detail ? _err.detail : ERROR_MESSAGES.GENERIC_ERROR
                );
            },
        });
    }

    /**
     *
     */
    private handleInitiated() {
        const pollingIntervalSec = this.poolingTimeFrame / 1000;
        this.uploadElapsedTime += pollingIntervalSec;

        const { startPercent, endPercent, durationSec } = chunkingStage;
        const progress = calculateStageProgress(
            this.uploadElapsedTime,
            durationSec,
            startPercent,
            endPercent
        );

        this.setProgress(Math.min(progress, endPercent));
    }

    /**
     *
     * @param chunks
     * @param completedChunks
     */
    private handleInProgress(chunks: number, completedChunks: number) {
        const { endPercent } = chunkingStage;
        const processingProgress = (completedChunks / chunks) * (100 - endPercent);
        this.setProgress(endPercent + processingProgress);
    }

    /**
     *
     * @param chunks
     * @param completedChunks
     */
    private handleMyUploadsCompleted(chunks: number, completedChunks: number) {
        this.stopPolling();
        const completionPercentage = chunks > 0 ? (completedChunks / chunks) * 100 : 0;
        const uploadCount = this.fileStateService.getMyUploads();

        this.setProgress(100);
        this.completeUpload();
        this.loaderService.setAttachmentDisabledFlag(false);
        this.clearAll();
        this.fileStateService.setMyUploads(uploadCount + 1);
        this.loaderService.setQueuedToast({
            type: completionPercentage > 50 ? 'success' : 'error',
            message:
                completionPercentage > 50
                    ? 'Your file has been uploaded'
                    : 'Something went wrong, please try again',
            dismissAfter: 5000,
        });
    }

    /**
     *
     * @param message
     * @param dismissAfter
     */
    private handleMyUploadsFailure(
        message = 'Something went wrong, please try again',
        dismissAfter = 10000
    ) {
        this.stopPolling();
        this.completeUpload();
        this.loaderService.setAttachmentDisabledFlag(false);
        this.clearAll();
        this.loaderService.setQueuedToast({
            type: 'error',
            message,
            dismissAfter,
        });
    }

    /**
     *
     * @param files
     */
    setFilesFromConversation(files: File[]) {
        this._filesFromConversation.set(files);
    }

    /**
     *
     * @param value
     */
    setSelectedFileTemporaryConsent(value: boolean) {
        this._selectedFileTemporaryConsent.set(value);
    }

    /**
     *
     * @param file
     */
    setFile(file: File | null) {
        this._selectedFile.set(file);

        if (!file || this._uploadOrigin() !== 'myUploads') {
            return;
        }

        // My Uploads file selection is handled here (service-level), not by a
        // page-level component, because the "Upload File" trigger and this
        // drag-and-drop overlay are available from any route (e.g.
        // admin/dashboard), while MerecerMindSearchInputComponent — the only
        // other listener for this signal — is only mounted on "/aida" and
        // "/aida/chat" and would otherwise silently miss the selection.
        this.handleMyUploadsFileSelected(file);
    }

    /**
     *
     * @param file
     */
    private handleMyUploadsFileSelected(file: File) {
        const acceptedTypes = FileUploadConfig.fileTypes.split(',').map((type) => type.trim());
        const isValidType = acceptedTypes.some((type) =>
            type.startsWith('.') ? file.name.endsWith(type) : file.type === type
        );

        if (!isValidType) {
            this.rejectMyUploadsFile(
                `Only ${acceptedTypes.join(', ')} files are allowed. Uploaded file did not meet this requirement.`
            );
            return;
        }

        if (FileUploadConfig.fileMaxSize && file.size > FileUploadConfig.fileMaxSize * 1000) {
            const maxFileSizeInMB = FileUploadConfig.fileMaxSize / 1024;
            let maxFileSizeInMBString = maxFileSizeInMB.toString();
            if (maxFileSizeInMBString.includes('.')) {
                maxFileSizeInMBString = maxFileSizeInMB.toFixed(2);
            }
            this.rejectMyUploadsFile(
                `Please upload a file smaller than ${maxFileSizeInMBString}MB.`
            );
            return;
        }

        this.setFileName(null);
        this.closeFileUpload();
        this.startMyUploadsUpload(file);
    }

    /**
     *
     * @param message
     */
    private rejectMyUploadsFile(message: string) {
        this.closeFileUpload();
        this._selectedFile.set(null);
        this.loaderService.setQueuedToast({
            type: 'error',
            message,
            dismissAfter: 8000,
        });
    }

    /**
     *
     * @param origin
     */
    setUploadOrigin(origin: UploadOrigin) {
        this._uploadOrigin.set(origin);
    }

    /**
     *
     * @param name
     */
    setFileName(name: string | null) {
        this._selectedFileName.set(name);
    }

    /**
     *
     * @param value
     */
    setProgress(value: number) {
        this.animateProgressTo(value);
        this.closeFileUpload();
    }

    /**
     * Smoothly eases `_progress` toward `target` instead of snapping to it.
     * Backend status polls arrive in coarse, uneven steps (e.g. a file can
     * jump straight from "initiated" to "completed" between two polls), which
     * made the bar look like it sat still and then instantly finished. This
     * animates every update over a short duration so motion always looks
     * continuous, regardless of how big/uneven the underlying jump is.
     * @param target
     */
    private animateProgressTo(target: number) {
        const clampedTarget = Math.max(0, Math.min(100, target));
        this.progressAnimationTarget = clampedTarget;
        this.cancelProgressAnimation();

        const startValue = this._progress();
        if (clampedTarget <= startValue) {
            this._progress.set(clampedTarget);
            return;
        }

        const durationMs = 900;
        const startTime = performance.now();

        const step = (now: number) => {
            const elapsed = now - startTime;
            const ratio = Math.min(elapsed / durationMs, 1);
            // Ease-out so the fill decelerates into the new value rather than
            // snapping or moving at a constant linear speed.
            const eased = 1 - (1 - ratio) * (1 - ratio);
            this._progress.set(startValue + (clampedTarget - startValue) * eased);

            if (ratio < 1 && this.progressAnimationTarget === clampedTarget) {
                this.progressAnimationHandle = requestAnimationFrame(step);
            } else {
                this.progressAnimationHandle = null;
            }
        };

        this.progressAnimationHandle = requestAnimationFrame(step);
    }

    /**
     *
     */
    private cancelProgressAnimation() {
        if (this.progressAnimationHandle !== null) {
            cancelAnimationFrame(this.progressAnimationHandle);
            this.progressAnimationHandle = null;
        }
    }

    /**
     *
     */
    reset() {
        this.cancelProgressAnimation();
        this._progress.set(0);
    }

    /**
     *
     */
    openFileUpload() {
        this._showFileUpload.set(true);
    }

    /**
     *
     */
    closeFileUpload() {
        this._showFileUpload.set(false);
    }

    /**
     *
     */
    get progress() {
        return this._progress;
    }

    /**
     *
     */
    get progressCompleted() {
        return this._progressCompleted();
    }

    /**
     *
     */
    get showFileUpload() {
        return this._showFileUpload;
    }

    /**
     *
     */
    get selectedFileValue() {
        return this._selectedFile();
    }

    /**
     *
     */
    get selectedFileName() {
        return this._selectedFileName();
    }

    /**
     *
     */
    get selectedFileTemporaryConsent() {
        return this._selectedFileTemporaryConsent();
    }

    /**
     *
     */
    get uploadOrigin() {
        return this._uploadOrigin();
    }

    /**
     *
     */
    get filesFromConversation() {
        return this._filesFromConversation();
    }

    /**
     * Clears conversation/attachment-scoped selection state. Skipped while any
     * upload (My Uploads or an in-conversation attachment) is in flight so
     * that unrelated page navigation (e.g. loading a different conversation)
     * cannot wipe out an active upload's origin/progress.
     */
    clearAll() {
        if (this.isUploading()) {
            return;
        }
        this._filesFromConversation.set([]);
        this.cancelProgressAnimation();
        this._progress.set(0);
        this._selectedFile.set(null);
        this._selectedFileName.set(null);
        this._selectedFileTemporaryConsent.set(false);
        this._uploadOrigin.set('conversation');
    }
}
